/**
 * Shared pricing calculation utilities for quotes and templates
 */
import { multiplyMoney } from "@/lib/price-utils";
import type { Service } from "@onsemble/shared";

/**
 * Calculate the total service cost in cents
 */
function calculateServicesCost(
  services: Service[] = [],
  selectedServices: string[] = [],
  serviceCustomPrices: Record<string, number> = {},
): number {
  return services
    .filter((service) => selectedServices.includes(service.uid))
    .reduce((sum, service) => {
      // Use custom price if set, otherwise use default price
      // Both are stored as cents (integers)
      const price =
        serviceCustomPrices[service.uid] !== undefined
          ? serviceCustomPrices[service.uid]
          : service.cost;
      return sum + price;
    }, 0);
}

/**
 * Calculate labor cost in cents
 * @param rateCents - Labor rate in cents per hour
 * @param laborHours - Number of hours
 */
export function calculateLaborCost(
  rateCents: number,
  laborHours: number,
): number {
  return rateCents * laborHours;
}

/**
 * Calculate parts cost with markup in cents (accepting cents as input)
 * @param partsCostCents - Parts cost in cents
 * @param markupPercent - Markup percentage
 * @param partsAffectPrice - Whether parts should affect the total price
 */
function calculatePartsCostFromCents(
  partsCostCents: number,
  markupPercent: number,
  partsAffectPrice: boolean = true,
): number {
  if (!partsAffectPrice) {
    return 0;
  }
  const markupAmount = multiplyMoney(partsCostCents, markupPercent / 100);
  return partsCostCents + markupAmount;
}

/**
 * Calculate the amount before discount: Labor + Services + Parts (with markup on parts only) in cents
 * This is what should be stored as amount_prediscount
 * Note: markup only applies to parts, not labor or services
 */
export function calculateAmountPreDiscount({
  rate = 0,
  laborHours = 0,
  partsCostCents = 0,
  markupPercent = 0,
  partsAffectPrice = true,
  services = [],
  selectedServices = [],
  serviceCustomPrices = {},
}: {
  rate?: number;
  laborHours?: number;
  partsCostCents?: number;
  markupPercent?: number;
  partsAffectPrice?: boolean;
  services?: Service[];
  selectedServices?: string[];
  serviceCustomPrices?: Record<string, number>;
}): number {
  const laborCostCents = calculateLaborCost(rate, laborHours);
  const servicesCostCents = calculateServicesCost(
    services,
    selectedServices,
    serviceCustomPrices,
  );
  const partsCostWithMarkupCents = calculatePartsCostFromCents(
    partsCostCents,
    markupPercent,
    partsAffectPrice,
  );
  return laborCostCents + servicesCostCents + partsCostWithMarkupCents;
}
